import ConditionalWrapper from '@atoms/ConditionalWrapper/ConditionalWrapper';
import Cta from '@atoms/Cta/Cta';
import Text from '@atoms/Text/Text';
import { IFooter } from '@models/IFooter';
import BreakpointEnum from '@models/enums/BreakpointEnum';
import SocialLinks from '@molecules/SocialLinks/SocialLinks';
import { breakpointsBelow } from '@utility/config/breakpoints';
import useBreakpoint from '@utility/hooks/useBreakpoint';
import { useEffect, useState } from 'react';

const Footer = ({
  cookiesSettingsLabel = 'Cookie Settings',
  copyrightNotice,
  cta,
  homepageLink,
  logo,
  navigationLinks,
  secondaryLinks,
  site,
  socialLinks,
  text,
}: IFooter) => {
  const breakpoint = useBreakpoint();
  const year = new Date().getFullYear();
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const handleTrackingEvent = (text: string) => {
    window.dataLayer.push({
      event: 'trackingEvent_navigation_footer_clicks',
      event_name_variable: 'footer_clicks',
      trackingEvent_navigation_footer_clicks: {
        clicktext: text,
      },
    });
  };

  const isMobileOrTabletPortrait =
    breakpoint && breakpointsBelow(BreakpointEnum.Large).includes(breakpoint);

  return (
    <section className="tw-text-grey-100' tw-h-auto tw-w-full tw-bg-white dark:tw-bg-grey-10">
      <div className="tw-container tw-mx-auto tw-grid tw-h-full tw-grid-cols-1 tw-gap-xs tw-px-xs tw-pb-sm tw-pt-xl md:tw-grid-cols-6 md:tw-gap-md md:tw-px-md lg:tw-grid-cols-12 lg:tw-gap-lg lg:tw-px-lg">
        {/* Column One */}
        <div className="tw-col-span-1 tw-grid tw-grid-cols-1 tw-grid-rows-3 tw-gap-xs md:tw-col-span-3 md:tw-gap-md lg:tw-col-span-6 lg:tw-grid-cols-2 lg:tw-gap-lg">
          <div className="tw-col-span-1 lg:tw-col-span-2">
            {logo?.url && (
              <a href={homepageLink || '/'}>
                <img
                  src={logo.url}
                  alt={logo.altText}
                  loading="lazy"
                  className="tw-h-lg tw-w-[140px]"
                />
              </a>
            )}
          </div>

          <div className="tw-col-span-1 tw-flex tw-items-end">
            {text && (
              <Text variant="body-small" className="tw-text-grey-80">
                {text}
              </Text>
            )}
          </div>
          {cta?.url && cta?.text && (
            <div className="tw-col-span-1 lg:tw-col-span-2">
              <Cta
                site={site}
                linkIcon="arrow-right"
                className="tw-col-start-2 tw-self-start"
                url={cta.url}
                text={cta.text}
                target={cta.target}
                enableDarkMode={false}
                onClick={() => handleTrackingEvent(cta.text)}
              />
            </div>
          )}
        </div>

        {/* Column Two */}
        <div className="tw-col-span-1 tw-grid tw-grid-cols-1 tw-gap-xs md:tw-col-span-3 md:tw-grid-rows-3 md:tw-gap-md lg:tw-col-span-6 lg:tw-gap-lg">
          <div className="tw-col-span-1 tw-grid tw-grid-cols-1 md:tw-row-span-2 md:tw-gap-md lg:tw-grid-cols-2 lg:tw-gap-lg">
            <ConditionalWrapper
              condition={!!isMobileOrTabletPortrait}
              wrapper={(children) => (
                <ul className="tw-flex tw-flex-col tw-items-start tw-gap-3">
                  {children}
                </ul>
              )}
            >
              {!!navigationLinks?.length && (
                <ConditionalWrapper
                  condition={!isMobileOrTabletPortrait}
                  wrapper={(children) => (
                    <ul className="tw-flex tw-flex-col tw-items-start tw-gap-3">
                      {children}
                    </ul>
                  )}
                >
                  {navigationLinks.map(({ url, text, target }) => (
                    <li key={text}>
                      <a
                        href={url}
                        onClick={() => handleTrackingEvent(text)}
                        target={target}
                      >
                        <Text variant="body-small">{text}</Text>
                      </a>
                    </li>
                  ))}
                </ConditionalWrapper>
              )}
              <ConditionalWrapper
                condition={!isMobileOrTabletPortrait}
                wrapper={(children) => (
                  <ul className="tw-flex tw-flex-col tw-items-start tw-gap-3">
                    {children}
                  </ul>
                )}
              >
                {!!secondaryLinks?.length &&
                  secondaryLinks.map(({ url, text, target }) => (
                    <li key={text}>
                      <a
                        href={url}
                        onClick={() => handleTrackingEvent(text)}
                        target={target}
                      >
                        <Text variant="body-small">{text}</Text>
                      </a>
                    </li>
                  ))}
                {isMounted && (
                  <li>
                    <div className="tw-font-sans tw-text-sm">
                      <button
                        type="button"
                        onClick={() => {
                          handleTrackingEvent(cookiesSettingsLabel);
                          if (typeof window.OneTrust !== 'undefined') {
                            window.OneTrust.ToggleInfoDisplay();
                          }
                        }}
                        aria-label={cookiesSettingsLabel}
                      >
                        <Text variant="body-small">{cookiesSettingsLabel}</Text>
                      </button>
                    </div>
                  </li>
                )}
              </ConditionalWrapper>
            </ConditionalWrapper>
          </div>

          <div className="tw-col-span-1 tw-grid tw-grid-cols-1 tw-grid-rows-2 tw-gap-xs md:tw-gap-md lg:tw-grid-cols-2 lg:tw-grid-rows-1 lg:tw-gap-lg">
            <div>
              <p className="tw-text-xs tw-text-grey-80">
                {year} &copy; {copyrightNotice}
              </p>
            </div>
            <div>{socialLinks && <SocialLinks links={socialLinks} />}</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Footer;
